import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  Download,
  CheckCheck,
  Upload,
  ThumbsUp,
  ThumbsDown,
  Undo2,
  PauseCircle,
  PlayCircle,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Modal } from "@/components/ui/Modal";
import { PageLoader } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import {
  useOfficialFeeRequest,
  useApproveOfficialFee,
  useUploadOfficialFeePayment,
  useAcknowledgeOfficialFee,
  useWithdrawOfficialFee,
  useHoldOfficialFee,
  useRestoreOfficialFee,
  useOfficialFeeHistory,
  useUploadOfficialFeeAttachments,
  useDeleteOfficialFeeAttachment,
} from "../hooks/useOfficialFees";
import { FEE_APPROVAL_THRESHOLD } from "../utils/feeThreshold";
import { withdrawOthersConfirm } from "../utils/withdraw";
import { OfficialFeeStatusBadge } from "../components/OfficialFeeStatusBadge";
import { OfficialFeeRejectModal } from "../components/OfficialFeeRejectModal";
import { OfficialFeeOrderLink } from "../components/OfficialFeeOrderLink";
import { OFFICIAL_FEE_HISTORY_VERB_LABELS } from "../types";
import type { OfficialFeeStatus, ManagedFileBrief } from "../types";
import { usePageTitle } from "@/hooks/usePageTitle";

const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col sm:grid sm:grid-cols-[180px_1fr] border-b border-[var(--color-border-muted)] last:border-0">
      <dt className="bg-[var(--color-bg-elevated)] px-4 py-1.5 sm:py-2.5 text-[10px] sm:text-xs font-medium uppercase tracking-wide text-[var(--color-text-secondary)] sm:border-r border-[var(--color-border-muted)]">
        {label}
      </dt>
      <dd className="px-4 py-2 sm:py-2.5 text-sm text-[var(--color-text-primary)] break-words">{value ?? "—"}</dd>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-4 py-2 bg-[var(--color-bg-base)] border-b border-[var(--color-border)] text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
      {children}
    </div>
  );
}

function formatAmount(amount: string) {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    // "always" forces a thousands separator for 4-digit pl-PL values; the pl-PL
    // CLDR default (minimumGroupingDigits=2) otherwise skips grouping below 10 000.
    useGrouping: "always",
  }).format(parseFloat(amount));
}

function formatDate(dt: string | null | undefined) {
  if (!dt) return "—";
  return new Date(dt).toLocaleString("pl-PL");
}

const NON_WITHDRAWABLE: OfficialFeeStatus[] = ["rejected", "payment_uploaded", "acknowledged", "withdrawn"];

const HOLD_HINT =
  "Wstrzymana prośba zostaje widoczna, ale wypada z kolejki do akceptacji — nikt jej " +
  "nie zaakceptuje ani nie odrzuci, dopóki nie zostanie przywrócona.";

export default function OfficialFeeRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { data: fee, isLoading } = useOfficialFeeRequest(id);
  usePageTitle(fee && `Opłata: ${fee.office_name}`);
  const { data: history } = useOfficialFeeHistory(id ?? null);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);

  const approveMut = useApproveOfficialFee();
  const uploadMut = useUploadOfficialFeePayment();
  const acknowledgeMut = useAcknowledgeOfficialFee();
  const withdrawMut = useWithdrawOfficialFee();
  const holdMut = useHoldOfficialFee();
  const restoreMut = useRestoreOfficialFee();
  const uploadAttachmentsMut = useUploadOfficialFeeAttachments();
  const deleteAttachmentMut = useDeleteOfficialFeeAttachment();
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  if (isLoading) return <PageLoader />;
  if (!fee) return <p className="p-8 text-[var(--color-text-secondary)]">Nie znaleziono prośby.</p>;

  const isDirectorOrCoord =
    user?.role === "director" || user?.role === "coordinator" || user?.is_ceo;
  const canPay = user?.has_small_account_access;
  const isRequester = user?.id === fee.requested_by;
  const isCeo = user?.is_ceo;
  // Koordynator decyduje (akceptuje/odrzuca) tylko o opłatach swoich projektów —
  // dla cudzych backend (CanApproveFeeForProject) i tak odpowie 403.
  const canDecide =
    user?.role === "coordinator" ? fee.can_decide : !!isDirectorOrCoord;
  // Koordynator akceptuje tylko do progu; powyżej niego przycisk zniknie, bo
  // backend i tak odpowie 403 (fee_approval_denial). Odrzucenie zostaje — progu
  // nie dotyczy.
  const aboveCoordinatorLimit =
    user?.role === "coordinator" && parseFloat(fee.amount) > FEE_APPROVAL_THRESHOLD;
  // Przejście „opłata → projekt ZE". Bramkowane jak trasa /power-projects (DLCModuleGuard);
  // przedsięwzięcie bez projektu ZE zostaje samym tekstem — workspace i tak by go nie otworzył.
  const canOpenZeProject =
    fee.has_ze_project &&
    user?.role !== "contractor" &&
    !!user?.accessible_dlc_modules?.includes("dlc_01");

  // Mirror of the backend `withdraw`: a decider withdraws their own request at any
  // pre-payment state and anybody's once approved (reject is gone by then); somebody
  // else's pending one is rejected, not withdrawn.
  function canWithdraw(): boolean {
    if (NON_WITHDRAWABLE.includes(fee!.status)) return false;
    if (isCeo) return true;
    if (canDecide && (isRequester || fee!.status === "approved")) return true;
    if (isRequester && (fee!.status === "pending_approval" || fee!.status === "on_hold"))
      return true;
    return false;
  }

  function handleWithdraw() {
    // A withdrawn request cannot be brought back — ask before doing it to someone else's.
    if (!isRequester && !confirm(withdrawOthersConfirm(fee!.requested_by_name))) return;
    withdrawMut.mutate(fee!.id);
  }

  // Wstrzymuje i przywraca składający albo ktokolwiek, kto o prośbie decyduje —
  // lustro `_may_pause` z backendu. Bez progu kwotowego (to nie akceptacja), więc
  // `canDecide`, nie `aboveCoordinatorLimit`.
  const canPause = isRequester || !!isCeo || canDecide;
  const canHold = canPause && fee.status === "pending_approval";
  const canRestore = canPause && fee.status === "on_hold";

  async function handleUpload() {
    if (!uploadFiles.length) return;
    await uploadMut.mutateAsync({ id: fee!.id, files: uploadFiles });
    setUploadOpen(false);
    setUploadFiles([]);
  }

  const hasDecision = Boolean(fee.approved_by_name || fee.rejection_comment);
  const hasPayment = Boolean(
    fee.paid_by_name || fee.payment_confirmation_files?.length || fee.acknowledged_at
  );
  const hasWithdrawal = Boolean(fee.withdrawn_by_name);
  const isHeld = fee.status === "on_hold";

  const canManageAttachments =
    fee.status !== "withdrawn" && (isRequester || isDirectorOrCoord || isCeo);

  async function handleAddAttachments(files: File[]) {
    if (!files.length) return;
    const oversize = files.find((f) => f.size > MAX_ATTACHMENT_BYTES);
    if (oversize) {
      setAttachmentError(`Plik "${oversize.name}" przekracza limit 25 MB.`);
      return;
    }
    setAttachmentError(null);
    await uploadAttachmentsMut.mutateAsync({ id: fee!.id, files });
  }

  async function handleDeleteAttachment(file: ManagedFileBrief) {
    if (!confirm(`Usunąć załącznik "${file.original_filename}"?`)) return;
    await deleteAttachmentMut.mutateAsync({ id: fee!.id, fileId: file.id });
  }

  return (
    <div className="p-3 sm:p-6 max-w-4xl mx-auto">
      <button
        onClick={() => navigate("/official-fees")}
        className="flex items-center gap-1 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] mb-3 sm:mb-4 transition-colors"
      >
        <ArrowLeft size={14} />
        Wróć do listy
      </button>

      {/* Header card */}
      <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] border-l-4 border-l-[var(--color-primary)] p-4 sm:p-5 mb-3 sm:mb-4">
        {/* Mobile: status badge on top, full-width; Desktop: hidden (badge goes top-right) */}
        <div className="flex items-center justify-between gap-2 mb-2 sm:hidden">
          <p className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
            Opłata urzędowa
          </p>
          <OfficialFeeStatusBadge status={fee.status} />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
          <div className="min-w-0 flex-1">
            <p className="hidden sm:block text-xs uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
              Opłata urzędowa
            </p>
            <h1 className="text-xl sm:text-2xl font-semibold text-[var(--color-text-primary)] leading-tight break-words">
              {fee.office_name}
            </h1>
            <p className="text-sm text-[var(--color-text-secondary)] mt-1 break-words">
              {canOpenZeProject ? (
                <Link
                  to={`/power-projects/${fee.project}`}
                  title="Przejdź do projektu ZE"
                  className="text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] hover:underline"
                >
                  {fee.project_name}
                  <ArrowUpRight size={12} className="inline shrink-0 ml-0.5 align-baseline" />
                </Link>
              ) : (
                fee.project_name
              )}{" "}
              <span className="text-[var(--color-text-muted)]">({fee.project_short_name})</span>
            </p>
            {fee.order && fee.order_number && (
              <p className="text-sm text-[var(--color-text-secondary)] mt-0.5 break-words">
                Zamówienie:{" "}
                <OfficialFeeOrderLink orderId={fee.order} orderNumber={fee.order_number} />
              </p>
            )}
            {/* Mobile: amount under title, large */}
            <p className="sm:hidden font-mono text-2xl font-semibold text-[var(--color-text-primary)] mt-3">
              {formatAmount(fee.amount)}
            </p>
          </div>
          <div className="hidden sm:flex flex-col items-end gap-2 shrink-0">
            <OfficialFeeStatusBadge status={fee.status} />
            <p className="font-mono text-xl font-semibold text-[var(--color-text-primary)]">
              {formatAmount(fee.amount)}
            </p>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      {(isDirectorOrCoord || canPay || isRequester || canWithdraw() || canRestore) && (
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 mb-3 sm:mb-4 [&>button]:w-full sm:[&>button]:w-auto">
          {canDecide && fee.status === "pending_approval" && (
            <>
              {!aboveCoordinatorLimit && (
                <Button onClick={() => approveMut.mutate(fee.id)} disabled={approveMut.isPending}>
                  <ThumbsUp size={14} className="mr-1" />
                  Akceptuj
                </Button>
              )}
              <Button
                variant="danger"
                onClick={() => setRejectOpen(true)}
              >
                <ThumbsDown size={14} className="mr-1" />
                Odrzuć
              </Button>
            </>
          )}
          {canPay && fee.status === "approved" && (
            <Button onClick={() => setUploadOpen(true)} disabled={uploadMut.isPending}>
              <Upload size={14} className="mr-1" />
              Prześlij potwierdzenie
            </Button>
          )}
          {isRequester && fee.status === "payment_uploaded" && (
            <Button
              onClick={() => acknowledgeMut.mutate(fee.id)}
              disabled={acknowledgeMut.isPending}
            >
              <CheckCheck size={14} className="mr-1" />
              Oznacz jako przetworzone
            </Button>
          )}
          {canHold && (
            <Button
              variant="secondary"
              title={HOLD_HINT}
              onClick={() => holdMut.mutate(fee.id)}
              disabled={holdMut.isPending}
            >
              <PauseCircle size={14} className="mr-1" />
              Wstrzymaj
            </Button>
          )}
          {canRestore && (
            <Button onClick={() => restoreMut.mutate(fee.id)} disabled={restoreMut.isPending}>
              <PlayCircle size={14} className="mr-1" />
              Przywróć do akceptacji
            </Button>
          )}
          {canWithdraw() && (
            <Button
              variant="ghost"
              onClick={handleWithdraw}
              disabled={withdrawMut.isPending}
            >
              <Undo2 size={14} className="mr-1" />
              Wycofaj zgłoszenie
            </Button>
          )}
        </div>
      )}

      {isHeld && (
        <p className="text-xs text-[var(--color-text-secondary)] mb-3 sm:mb-4 border-l-2 border-orange-400 pl-3 py-1">
          {HOLD_HINT}
        </p>
      )}

      {aboveCoordinatorLimit && fee.status === "pending_approval" && (
        <p className="text-xs text-[var(--color-text-secondary)] mb-3 sm:mb-4">
          Kwota powyżej {FEE_APPROVAL_THRESHOLD} zł — akceptacja należy do dyrektora.
          Możesz prośbę odrzucić.
        </p>
      )}

      {/* Beneficiary card */}
      <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] mb-4 overflow-hidden">
        <SectionTitle>Beneficjent i przelew</SectionTitle>
        <dl>
          <Row label="Płacąca spółka" value={fee.paying_company_name ?? fee.paying_company_short_code} />
          <Row label="Urząd" value={fee.office_name} />
          <Row label="Numer konta" value={<span className="font-mono break-all text-xs sm:text-sm">{fee.office_account_number}</span>} />
          <Row label="Adres urzędu" value={<span className="whitespace-pre-wrap">{fee.office_address}</span>} />
          <Row label="Tytuł przelewu" value={<span className="font-mono break-all text-xs sm:text-sm">{fee.transfer_name}</span>} />
          {fee.is_power_of_attorney && <Row label="Opłata za pełnomocnictwo" value="Tak" />}
          {fee.description && <Row label="Opis" value={fee.description} />}
        </dl>
      </div>

      {/* Submission card */}
      <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] mb-4 overflow-hidden">
        <SectionTitle>Zgłoszenie</SectionTitle>
        <dl>
          <Row label="Wnioskujący" value={fee.requested_by_name} />
          <Row label="Data złożenia" value={formatDate(fee.created_at)} />
        </dl>
      </div>

      {/* Attachments card */}
      {(fee.request_attachment_files?.length > 0 || canManageAttachments) && (
        <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] mb-4 overflow-hidden">
          <SectionTitle>Załączniki do prośby</SectionTitle>
          <div className="px-4 py-3">
            {fee.request_attachment_files?.length > 0 ? (
              <ul className="space-y-1.5 mb-3">
                {fee.request_attachment_files.map((f) => (
                  <li
                    key={f.id}
                    className="flex items-center justify-between gap-2 px-3 py-1.5 bg-[var(--color-bg-elevated)]"
                  >
                    {f.url ? (
                      <a
                        href={f.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] hover:underline text-sm truncate"
                      >
                        <Download size={13} />
                        {f.original_filename}
                      </a>
                    ) : (
                      <span className="text-sm truncate">{f.original_filename}</span>
                    )}
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-[var(--color-text-muted)] font-mono">
                        {formatBytes(f.size_bytes)}
                      </span>
                      {canManageAttachments && (
                        <button
                          type="button"
                          onClick={() => handleDeleteAttachment(f)}
                          disabled={deleteAttachmentMut.isPending}
                          className="text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] disabled:opacity-50"
                          title="Usuń załącznik"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-[var(--color-text-muted)] mb-3">
                Brak załączników.
              </p>
            )}
            {canManageAttachments && (
              <div>
                <FileDropZone
                  multiple
                  disabled={uploadAttachmentsMut.isPending}
                  label={
                    uploadAttachmentsMut.isPending
                      ? "Wysyłanie…"
                      : "Przeciągnij załączniki lub kliknij, aby wybrać (maks. 25 MB/plik)"
                  }
                  onSelect={(picked) => void handleAddAttachments(picked)}
                />
                {attachmentError && (
                  <p className="text-xs text-red-500 mt-1">{attachmentError}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Decision card */}
      {hasDecision && (
        <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] mb-4 overflow-hidden">
          <SectionTitle>Decyzja</SectionTitle>
          <dl>
            {fee.approved_by_name && (
              <>
                <Row label="Zaakceptował/odrzucił" value={fee.approved_by_name} />
                <Row label="Data decyzji" value={formatDate(fee.approved_at)} />
              </>
            )}
            {fee.rejection_comment && (
              <Row
                label="Powód odrzucenia"
                value={<span className="text-[var(--color-primary)]">{fee.rejection_comment}</span>}
              />
            )}
          </dl>
        </div>
      )}

      {/* Payment card */}
      {hasPayment && (
        <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] mb-4 overflow-hidden">
          <SectionTitle>Płatność</SectionTitle>
          <dl>
            {fee.paid_by_name && (
              <>
                <Row label="Opłacił" value={fee.paid_by_name} />
                <Row label="Data opłacenia" value={formatDate(fee.paid_at)} />
              </>
            )}
            {fee.payment_confirmation_files?.length > 0 && (
              <Row
                label="Potwierdzenia"
                value={
                  <div className="flex flex-col gap-1.5">
                    {fee.payment_confirmation_files.map((f) =>
                      f.url ? (
                        <a
                          key={f.id}
                          href={f.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] hover:underline text-sm w-fit"
                        >
                          <Download size={13} />
                          {f.original_filename}
                        </a>
                      ) : (
                        <span key={f.id} className="text-sm">{f.original_filename}</span>
                      )
                    )}
                  </div>
                }
              />
            )}
            {fee.acknowledged_at && (
              <Row label="Potwierdzono odbiór" value={formatDate(fee.acknowledged_at)} />
            )}
          </dl>
        </div>
      )}

      {/* Hold card — tylko gdy prośba JEST wstrzymana: pola opisują stan bieżący i
          przywrócenie je czyści (ślad zostaje w historii niżej). */}
      {isHeld && (
        <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] mb-4 overflow-hidden">
          <SectionTitle>Wstrzymanie</SectionTitle>
          <dl>
            <Row label="Wstrzymał/a" value={fee.held_by_name} />
            <Row label="Data wstrzymania" value={formatDate(fee.held_at)} />
          </dl>
        </div>
      )}

      {/* Withdrawal card */}
      {hasWithdrawal && (
        <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] mb-4 overflow-hidden">
          <SectionTitle>Wycofanie</SectionTitle>
          <dl>
            <Row label="Wycofał/a" value={fee.withdrawn_by_name} />
            <Row label="Data wycofania" value={formatDate(fee.withdrawn_at)} />
          </dl>
        </div>
      )}

      {/* Skąd wzięła się opłata — zamówienie w DLC_01, które ją złożyło. Pusta lista
          (opłata wpisana ręcznie w /official-fees) chowa całą kartę. */}
      {fee.source_links?.length > 0 && (
        <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] mb-4 overflow-hidden">
          <SectionTitle>Źródło opłaty</SectionTitle>
          <dl>
            {fee.source_links.map((src) => (
              <Row
                key={src.url}
                label={src.is_poa_fee ? "Pełnomocnictwo do" : "Zamówienie"}
                value={
                  <Link
                    to={src.url}
                    className="inline-flex items-center gap-1.5 text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] hover:underline break-words"
                  >
                    <ArrowUpRight size={14} className="shrink-0" />
                    <span>
                      {src.title} <span className="font-mono">{src.number}</span>
                      <span className="text-[var(--color-text-muted)]"> ({src.status_display})</span>
                    </span>
                  </Link>
                }
              />
            ))}
          </dl>
        </div>
      )}

      {/* History */}
      {history && history.length > 0 && (
        <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] overflow-hidden">
          <SectionTitle>Historia zgłoszenia</SectionTitle>
          <ol className="relative space-y-4 sm:space-y-5 px-3 sm:px-5 py-4 ml-2 sm:ml-3 border-l border-[var(--color-border)]">
            {history.map((entry) => (
              <li key={entry.id} className="relative pl-3 sm:pl-4">
                <span className="absolute -left-[7px] top-1.5 h-3 w-3 bg-[var(--color-primary)] border-2 border-[var(--color-bg-surface)]" />
                <p className="text-[11px] sm:text-xs font-mono text-[var(--color-text-muted)]">
                  {new Date(entry.timestamp).toLocaleString("pl-PL")}
                </p>
                <p className="text-sm text-[var(--color-text-primary)] mt-0.5 break-words">
                  <span className="font-semibold">{entry.actor_name}</span>
                  <span className="text-[var(--color-text-secondary)]">
                    {" — "}
                    {OFFICIAL_FEE_HISTORY_VERB_LABELS[entry.verb] ?? entry.verb}
                  </span>
                </p>
                {entry.description && (
                  <p className="text-xs text-[var(--color-text-secondary)] mt-1 italic break-words">
                    {entry.description}
                  </p>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Reject modal */}
      <OfficialFeeRejectModal
        feeId={fee.id}
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
      />

      {/* Upload modal */}
      <Modal
        open={uploadOpen}
        onClose={() => {
          setUploadOpen(false);
          setUploadFiles([]);
        }}
        title="Prześlij potwierdzenia przelewu (PDF)"
      >
        <div className="space-y-4">
          <FileDropZone
            files={uploadFiles}
            onFilesChange={setUploadFiles}
            multiple
            accept=".pdf"
            label="Przeciągnij pliki PDF lub kliknij, aby wybrać"
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setUploadOpen(false);
                setUploadFiles([]);
              }}
            >
              Anuluj
            </Button>
            <Button disabled={!uploadFiles.length || uploadMut.isPending} onClick={handleUpload}>
              Prześlij
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
